import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Send, Search } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminTitle, DataTable } from "@/pages/admin/AdminLayout";
import { Spinner, Empty } from "@/components/site/Bits";
import { useApi, errMsg, fmtDate, fmtPrice } from "@/lib/api";

const EMPTY = { code: "", kind: "percent", value: 10, max_uses: 0, per_user_once: true, course_ids: [], starts_at: "", expires_at: "", active: true, note: "" };
const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;

const CouponForm = ({ initial, courses, onClose, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleCourse = (id) => setF({ ...f, course_ids: f.course_ids.includes(id) ? f.course_ids.filter((x) => x !== id) : [...f.course_ids, id] });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, code: f.code.trim().toUpperCase(), value: Number(f.value), max_uses: Number(f.max_uses) || 0 };
    try {
      await api(initial.id ? "put" : "post", initial.id ? `/admin/coupons/${initial.id}` : "/admin/coupons", body);
      toast.success("تم حفظ القسيمة");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="coupon-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{initial.id ? "تعديل قسيمة" : "قسيمة جديدة"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          <F label="رمز القسيمة (أحرف إنجليزية وأرقام)"><Input dir="ltr" className="uppercase" required value={f.code} onChange={set("code")} data-testid="coupon-code-input" /></F>
          <F label="نوع الخصم">
            <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
              <SelectTrigger data-testid="coupon-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="percent">نسبة مئوية %</SelectItem><SelectItem value="fixed">مبلغ ثابت (ر.ع.)</SelectItem></SelectContent>
            </Select>
          </F>
          <F label={f.kind === "percent" ? "نسبة الخصم %" : "مبلغ الخصم (ر.ع.)"}><Input type="number" step="0.001" min="0" required value={f.value} onChange={set("value")} data-testid="coupon-value-input" /></F>
          <F label="الحد الأقصى للاستخدام (0 = بلا حد)"><Input type="number" min="0" value={f.max_uses} onChange={set("max_uses")} data-testid="coupon-max-uses-input" /></F>
          <F label="تاريخ البداية (اختياري)"><Input type="date" value={f.starts_at} onChange={set("starts_at")} data-testid="coupon-starts-input" /></F>
          <F label="تاريخ الانتهاء (اختياري)"><Input type="date" value={f.expires_at} onChange={set("expires_at")} data-testid="coupon-expires-input" /></F>
          <div className="sm:col-span-2"><F label="ملاحظة داخلية"><Input value={f.note} onChange={set("note")} data-testid="coupon-note-input" /></F></div>
          <div className="sm:col-span-2 space-y-2">
            <Label className="text-xs">الدورات المشمولة (بدون تحديد = جميع الدورات)</Label>
            <div className="grid sm:grid-cols-2 gap-2">
              {courses.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm border border-[var(--line)] rounded-lg p-2">
                  <Checkbox checked={f.course_ids.includes(c.id)} onCheckedChange={() => toggleCourse(c.id)} data-testid={`coupon-course-${c.id}`} />
                  <span className="line-clamp-1">{c.title}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.per_user_once} onCheckedChange={(v) => setF({ ...f, per_user_once: v })} data-testid="coupon-once-switch" />مرة واحدة لكل متدرب</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="coupon-active-switch" />القسيمة مفعّلة</label>
          <div className="sm:col-span-2 flex gap-2 pt-2">
            <button disabled={busy} className="btn-copper" data-testid="coupon-save-btn">{busy ? "جارٍ الحفظ..." : "حفظ"}</button>
            <button type="button" onClick={onClose} className="btn-gold-outline" data-testid="coupon-cancel-btn">إلغاء</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const discountText = (c) => (c.kind === "percent" ? `${c.value}%` : fmtPrice(c.value));

const SendDialog = ({ coupon, courses, onClose, onSent }) => {
  const api = useApi();
  const [audience, setAudience] = useState("users");
  const [courseId, setCourseId] = useState("");
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api("get", "/users").then((r) => setUsers(r.users.filter((u) => u.role !== "admin"))).catch(() => {});
  }, [api]);
  const shown = users.filter((u) => !q || [u.name, u.email, u.phone].some((x) => (x || "").toLowerCase().includes(q.toLowerCase())));
  const togglePick = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  const send = async () => {
    if (audience === "all" && !window.confirm(`سيتم إرسال القسيمة بالبريد إلى جميع المتدربين (${users.length}). متابعة؟`)) return;
    setBusy(true);
    try {
      const r = await api("post", `/admin/coupons/${coupon.id}/send`, { audience, user_ids: picked, course_id: courseId, message });
      toast.success(`جارٍ إرسال القسيمة إلى ${r.count} متدرب`);
      onSent();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const ready = audience === "all" || (audience === "course" && courseId) || (audience === "users" && picked.length);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="coupon-send-dialog">
        <DialogHeader><DialogTitle className="font-display">إرسال القسيمة <span dir="ltr">{coupon.code}</span> ({discountText(coupon)})</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <F label="إرسال إلى">
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="coupon-send-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع المتدربين ({users.length})</SelectItem>
                <SelectItem value="course">المتدربون المؤكدون في دورة</SelectItem>
                <SelectItem value="users">متدرب معيّن أو أكثر</SelectItem>
              </SelectContent>
            </Select>
          </F>
          {audience === "course" && (
            <F label="الدورة">
              <Select value={courseId} onValueChange={setCourseId}>
                <SelectTrigger data-testid="coupon-send-course"><SelectValue placeholder="اختر الدورة" /></SelectTrigger>
                <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          )}
          {audience === "users" && (
            <div className="space-y-2">
              <div className="relative"><Search size={16} className="absolute right-3 top-3 text-[var(--muted-ink)]" /><Input className="pr-9" placeholder="ابحث بالاسم أو البريد أو الهاتف" value={q} onChange={(e) => setQ(e.target.value)} data-testid="coupon-send-search" /></div>
              <div className="max-h-56 overflow-y-auto border border-[var(--line)] rounded-lg divide-y divide-[var(--line)]">
                {shown.map((u) => (
                  <label key={u.user_id} className="flex items-center gap-2 p-2 text-sm cursor-pointer">
                    <Checkbox checked={picked.includes(u.user_id)} onCheckedChange={() => togglePick(u.user_id)} data-testid={`coupon-send-user-${u.user_id}`} />
                    <span>{u.name || "—"}</span><span dir="ltr" className="text-xs text-[var(--muted-ink)] mr-auto">{u.email}</span>
                  </label>
                ))}
                {!shown.length && <p className="p-3 text-sm text-[var(--muted-ink)]">لا توجد نتائج</p>}
              </div>
              <p className="text-xs text-[var(--muted-ink)]" data-testid="coupon-send-picked-count">تم اختيار {picked.length}</p>
            </div>
          )}
          <F label="رسالة مرافقة (اختياري)"><Textarea rows={3} maxLength={500} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="مثال: بمناسبة افتتاح التسجيل، إليك خصم خاص..." data-testid="coupon-send-message" /></F>
          <p className="text-xs text-[var(--muted-ink)]">تصل القسيمة بالبريد الإلكتروني وتظهر في إشعارات حساب المتدرب.</p>
          <div className="flex gap-2">
            <button onClick={send} disabled={busy || !ready} className="btn-copper" data-testid="coupon-send-submit"><Send size={16} />{busy ? "جارٍ الإرسال..." : "إرسال"}</button>
            <button onClick={onClose} className="btn-gold-outline" data-testid="coupon-send-cancel">إلغاء</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const couponState = (c) => {
  if (!c.active) return ["موقوفة", "bg-stone-200 text-stone-600"];
  if (c.expires_at && c.expires_at < todayStr()) return ["منتهية", "bg-red-100 text-red-700"];
  if (c.max_uses && (c.used_count || 0) >= c.max_uses) return ["مستنفدة", "bg-amber-100 text-amber-800"];
  return ["مفعّلة", "bg-emerald-100 text-emerald-700"];
};

export default function AdminCoupons() {
  const api = useApi();
  const [list, setList] = useState(null);
  const [courses, setCourses] = useState([]);
  const [edit, setEdit] = useState(null);
  const [sending, setSending] = useState(null);
  const load = useCallback(async () => {
    const [cps, cs] = await Promise.all([api("get", "/admin/coupons"), api("get", "/admin/courses")]);
    setList(cps);
    setCourses(cs);
  }, [api]);
  useEffect(() => {
    load();
  }, [load]);
  const toggle = async (c) => {
    const { id, used_count, created_at, sent_count, last_sent_at, ...rest } = c;
    try { await api("put", `/admin/coupons/${id}`, { ...rest, active: !c.active }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`حذف القسيمة ${c.code}؟`)) return;
    try { await api("delete", `/admin/coupons/${c.id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const courseName = (id) => courses.find((x) => x.id === id)?.title || id;
  if (!list) return <Spinner />;
  return (
    <div data-testid="admin-coupons-page">
      <AdminTitle title="القسائم">
        <button onClick={() => setEdit({})} className="btn-copper" data-testid="add-coupon-btn"><Plus size={16} />قسيمة جديدة</button>
      </AdminTitle>
      {list.length === 0 ? <Empty text="لا توجد قسائم بعد" testId="coupons-empty" /> : (
        <DataTable head={["الرمز", "الخصم", "الاستخدام", "الصلاحية", "الدورات", "الحالة", ""]} testId="coupons-table">
          {list.map((c) => (
            <tr key={c.id} data-testid={`coupon-row-${c.code}`}>
              <td dir="ltr" className="text-right font-bold">{c.code}{c.note && <div className="text-[11px] font-normal text-[var(--muted-ink)]" dir="rtl">{c.note}</div>}</td>
              <td className="whitespace-nowrap">{discountText(c)}</td>
              <td className="whitespace-nowrap" data-testid={`coupon-used-${c.code}`}>{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : " / بلا حد"}</td>
              <td className="text-xs whitespace-nowrap">{c.starts_at ? `من ${fmtDate(c.starts_at)}` : ""}{c.expires_at ? <div>حتى {fmtDate(c.expires_at)}</div> : !c.starts_at && "دائمة"}</td>
              <td className="text-xs max-w-[220px]">{c.course_ids?.length ? c.course_ids.map(courseName).join("، ") : "جميع الدورات"}</td>
              <td>
                <button onClick={() => toggle(c)} className={`text-xs rounded-full px-3 py-1 ${couponState(c)[1]}`} data-testid={`coupon-toggle-${c.code}`}>
                  {couponState(c)[0]}
                </button>
                {c.sent_count > 0 && <div className="text-[11px] text-[var(--muted-ink)] mt-1" data-testid={`coupon-sent-${c.code}`}>أُرسلت إلى {c.sent_count}</div>}
              </td>
              <td className="whitespace-nowrap">
                <button onClick={() => setSending(c)} className="p-2 hover:text-[var(--copper)]" title="إرسال للمتدربين" data-testid={`coupon-send-${c.code}`}><Send size={16} /></button>
                <button onClick={() => setEdit(c)} className="p-2 hover:text-[var(--copper)]" data-testid={`coupon-edit-${c.code}`}><Pencil size={16} /></button>
                <button onClick={() => remove(c)} className="p-2 hover:text-red-600" data-testid={`coupon-delete-${c.code}`}><Trash2 size={16} /></button>
              </td>
            </tr>
          ))}
        </DataTable>
      )}
      {sending && <SendDialog coupon={sending} courses={courses} onClose={() => setSending(null)} onSent={() => { setSending(null); load(); }} />}
      {edit && <CouponForm initial={edit} courses={courses} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
